import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useQueryParams from '@common/src/hooks/useQueryParams';
import { useFlowProductsOverviewParams } from './useFlowProductsOverviewParams';
import { FlowProductsNavigationMap } from './createSidebarNavigationMap';
import { FLOWS_PRODUCTS_PATH } from '@flows/src/FlowsProductsRouter';

/**
 * Uses the sidebar navigation map to navigate to the next or previous product in the products overview page
 */
export function useFlowProductsSidebarNavigation(
  productNavigationMap: FlowProductsNavigationMap | null | undefined,
) {
  const { flowId } = useParams<{ flowId: string }>();
  const { tagId } = useFlowProductsOverviewParams();
  const queryParams = useQueryParams();

  const indexParam = queryParams.get('index');
  const currentIndex = indexParam ? parseInt(indexParam, 10) : null;

  const navigationHelpers = useMemo(() => {
    if (!productNavigationMap || !tagId || !flowId) {
      return {
        getNextProductUrl: () => null,
        getPreviousProductUrl: () => null,
        isFirstIndex: () => true,
        isFirstTag: () => true,
        isLastTag: () => true,
        getInsuranceName: () => '',
      };
    }

    //
    // Helper functions that use hook context
    //

    const getNextProductUrl = (): string | null => {
      // First check if there's a next index within the current tag
      const nextIndex = getNextIndexInTag(
        productNavigationMap,
        tagId,
        currentIndex,
      );

      if (nextIndex !== null) {
        return createNavigationUrl(
          flowId,
          tagId,
          nextIndex,
          queryParams,
          productNavigationMap,
        );
      }

      // No more indices in current tag, move to next category
      const nextTagId = getNextTagId(productNavigationMap, tagId);
      if (nextTagId) {
        const nextTagIndices = getIndicesForTag(
          productNavigationMap,
          nextTagId,
        );
        const indexToUse =
          nextTagIndices.length > 1 ? nextTagIndices[0] : undefined;

        return createNavigationUrl(
          flowId,
          nextTagId,
          indexToUse,
          queryParams,
          productNavigationMap,
        );
      }

      return null;
    };

    const getPreviousProductUrl = (): string | null => {
      // First check if there's a previous index within the current tag
      const prevIndex = getPrevIndexInTag(
        productNavigationMap,
        tagId,
        currentIndex,
      );

      if (prevIndex !== null) {
        return createNavigationUrl(
          flowId,
          tagId,
          prevIndex,
          queryParams,
          productNavigationMap,
        );
      }

      // No more indices in current tag, move to previous category
      const prevTagId = getPrevTagId(productNavigationMap, tagId);
      if (prevTagId) {
        const prevTagIndices = getIndicesForTag(
          productNavigationMap,
          prevTagId,
        );
        const lastIndex =
          prevTagIndices.length > 1
            ? prevTagIndices[prevTagIndices.length - 1]
            : undefined;

        return createNavigationUrl(
          flowId,
          prevTagId,
          lastIndex,
          queryParams,
          productNavigationMap,
        );
      }

      return null;
    };

    const isFirstIndex = (): boolean => {
      const indices = getIndicesForTag(productNavigationMap, tagId);
      if (indices.length === 0) {
        return true;
      }
      return (
        currentIndex === null ||
        (indices.length > 0 && currentIndex === indices[0])
      );
    };

    const isFirstTag = (): boolean => {
      return getTagIds(productNavigationMap).indexOf(tagId) === 0;
    };

    const isLastTag = (): boolean => {
      const tagIds = getTagIds(productNavigationMap);
      return tagIds.length > 0 && tagId === tagIds[tagIds.length - 1];
    };

    const getInsuranceName = (): string => {
      const tagItem = productNavigationMap.find((item) => item.key === tagId);
      if (!tagItem) {
        return '';
      }

      // If index is provided and there are subItems, try to find the specific subItem
      if (currentIndex !== null && tagItem.subItems) {
        const subItem = tagItem.subItems.find(
          (sub) => sub.index === currentIndex,
        );
        if (subItem) {
          // subItem.label already contains risk_object_name if available, or fallback to `${tag.name} ${index + 1}`
          return subItem.label;
        }
      }

      // Fallback to tag name
      return tagItem.label;
    };

    return {
      getNextProductUrl,
      getPreviousProductUrl,
      isFirstIndex,
      isFirstTag,
      isLastTag,
      getInsuranceName,
    };
  }, [productNavigationMap, tagId, flowId, queryParams, currentIndex]);

  return {
    ...navigationHelpers,
    tagId,
    currentIndex,
    flowId,
  };
}

/**
 * Gets the insurance name (risk object name or tag name) for a specific tag/index combination
 * from the navigation map. This matches the logic used in the sidebar.
 */
export function getFlowProductsSidebarActiveInsuranceName(
  navigationMap: FlowProductsNavigationMap | null,
  tagId: string | null,
  index: number | null,
): string {
  if (!tagId || !navigationMap?.length) {
    return '';
  }

  const tagItem = navigationMap.find((item) => item.key === tagId);
  if (!tagItem) {
    return '';
  }

  // If index is provided and there are subItems, try to find the specific subItem
  if (index !== null && tagItem.subItems) {
    const subItem = tagItem.subItems.find((sub) => sub.index === index);
    if (subItem) {
      // subItem.label already contains risk_object_name if available, or fallback to `${tag.name} ${index + 1}`
      return subItem.label;
    }
  }

  // Fallback to tag name
  return tagItem.label;
}

//
// Internal helper functions
//

function getTagIds(navigationMap: FlowProductsNavigationMap) {
  return navigationMap.map((item) => item.key);
}

function getIndicesForTag(
  navigationMap: FlowProductsNavigationMap,
  tagId: string,
): number[] {
  const tagItem = navigationMap.find((item) => item.key === tagId);
  if (!tagItem) {
    return [];
  }

  // If no subItems, it means there's only one index (0)
  if (!tagItem.subItems) {
    return [0];
  }

  return tagItem.subItems.map((subItem) => subItem.index).sort((a, b) => a - b);
}

function getNextIndexInTag(
  navigationMap: FlowProductsNavigationMap,
  tagId: string,
  currentIndex: number | null,
): number | null {
  const indices = getIndicesForTag(navigationMap, tagId);
  if (indices.length === 0) {
    return null;
  }

  // If no current index and multiple indices exist, return first index
  if (currentIndex === null) {
    return indices.length > 1 ? indices[0] : null;
  }

  const currentPosition = indices.indexOf(currentIndex);
  if (currentPosition >= 0 && currentPosition < indices.length - 1) {
    return indices[currentPosition + 1];
  }

  return null;
}

function getPrevIndexInTag(
  navigationMap: FlowProductsNavigationMap,
  tagId: string,
  currentIndex: number | null,
): number | null {
  const indices = getIndicesForTag(navigationMap, tagId);
  if (indices.length === 0 || currentIndex === null) {
    return null;
  }

  const currentPosition = indices.indexOf(currentIndex);
  if (currentPosition > 0) {
    return indices[currentPosition - 1];
  }

  return null;
}

function getNextTagId(
  navigationMap: FlowProductsNavigationMap,
  currentTagId: string,
): string | null {
  const tagIds = getTagIds(navigationMap);
  const currentIndex = tagIds.indexOf(currentTagId);
  if (currentIndex >= 0 && currentIndex < tagIds.length - 1) {
    return tagIds[currentIndex + 1];
  }
  return null;
}

function getPrevTagId(
  navigationMap: FlowProductsNavigationMap,
  currentTagId: string,
): string | null {
  const tagIds = getTagIds(navigationMap);
  const currentIndex = tagIds.indexOf(currentTagId);
  if (currentIndex > 0) {
    return tagIds[currentIndex - 1];
  }
  return null;
}

function createNavigationUrl(
  flowId: string,
  tagId: string,
  index: number | null | undefined,
  searchParams: URLSearchParams,
  navigationMap: FlowProductsNavigationMap,
): string {
  const pathname = `${FLOWS_PRODUCTS_PATH}/${flowId}/overview/${tagId}`;
  const search = new URLSearchParams(searchParams.toString());

  // set the index param in url search params
  if (index !== null && index !== undefined) {
    const indices = getIndicesForTag(navigationMap, tagId);
    if (indices.length > 0) {
      search.set('index', index.toString());
    } else {
      search.delete('index');
    }
  } else {
    search.delete('index');
  }

  const searchString = search.toString();
  return searchString ? `${pathname}?${searchString}` : pathname;
}
